import React, { FC, useCallback, useEffect, useRef, useState } from "react";

import CustomGrid from "ui/grid/CustomGrid";
import { GridCellProps, GridItemChangeEvent } from "@progress/kendo-react-grid";

import CustomButton from "ui/Button";
import { CLASSNAME, COLOR } from "utils/constant";
import {
	IRegistrationForm,
	RegistrationDetail,
	RegistrationLine,
	RegistrationLineForm,
} from "interface/registration";
import { Package } from "interface/common";
import {
	addNewRegistrationLineApi,
	deleteTemporaryRegistrationLineApi,
} from "api/registration";
import {
	salesNoteProductConditionFilterApi,
	updateSalesNoteProductConditionFilterApi,
} from "api/salesNote";
import Info from "ui/Info";
import { useParams } from "react-router";
import { stepTwoGridColumns } from "../utils/StepTwoGridColumns";
import { useTranslation } from "react-i18next";
import { useInfoStore } from "store/error";
import StepTwoMobileList from "./StepTwoMobileList";
import { CustomCheckbox } from "ui/Input";

interface LineForm extends RegistrationLineForm {
	inEdit?: boolean;
	columnIndex?: number;
	focusedField?: string;
	onfocus?: boolean;
}
interface RegistrationForm extends RegistrationDetail {
	lines?: LineForm[];
}
export const FormStepTwo: FC<{
	form: IRegistrationForm;
	setForm: React.Dispatch<React.SetStateAction<IRegistrationForm>>;
	showRequirement: boolean;
	setMeetRequirement: (meetRequirement: boolean) => void;
	addLineRef?: React.MutableRefObject<(() => void) | null>;
}> = ({
	form
	, setForm
	, showRequirement
	, setMeetRequirement
	, addLineRef
}) => {
	const { t } = useTranslation();
	const [showDeleteLine, setShowDeleteLine] = useState(false);
	const lastChangedPackage = useRef<Package>();
	const deletingLineRef = useRef<RegistrationLine>();
	const [lineForm, setLineForm] = useState<LineForm[]>(form?.lines || []);

	const [productConditionFilter, setProductConditionFilter] = useState<
		{ value: boolean; name: string; displayName: string }[]
	>([]);


	const focusRef = useRef<{
		columnIndex: number;
		focusedField: string;
		rowIndex: number;
		id: number;
	}>();
	const { id: registrationId } = useParams();
	const { setError } = useInfoStore();
	const [isMobileView, setIsMobileView] = useState(() => window.innerWidth < 768);
	const bottomRef = useRef<HTMLDivElement>(null);
	const prevFormLengthRef = useRef<number>(0);

	useEffect(() => {
		const handleResize = () => {
			setIsMobileView(window.innerWidth < 768);
		};

		window.addEventListener("resize", handleResize);
		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, []);

	useEffect(() => {
		salesNoteProductConditionFilterApi()
			.then((data) => {
				setProductConditionFilter(data);
			})
			.catch((err) => {
				console.log("failed to get product condition filter", err);
			});
	}, []);


	useEffect(() => {
		if (form?.lines) {
			setLineForm(form.lines);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [form?.lines]);

	const checkAllRequirement = useCallback(() => {
		if (!lineForm || lineForm.length === 0) {
			return false;
		}

		for (const line of lineForm) {
			if (
				line.productId === 0 ||
				line.packagingCode === "" ||
				line.packagingCode == null ||
				line.price === 0 ||
				line.registrationLiveWeightKg === 0
			) {
				return false;
			}
			if (line.piecesMandatory === 1) {
				if (!line.numberOfPieces || line.numberOfPieces === 0) {
					return false;
				}
			}
		}

		return true;
	}, [lineForm]);

	useEffect(() => {
		setMeetRequirement(checkAllRequirement());
	}, [lineForm, checkAllRequirement, setMeetRequirement]);

	useEffect(() => {
		if (lineForm.length > 0) {
			setForm((prev) => ({ ...prev, lines: lineForm }));
		}
	}, [lineForm, setForm]);

	const addRegistrationLine = (_form: LineForm, dropDown: boolean = false) => {
		_form.registrationId = registrationId as any;
		if (!_form.id) {
			_form.packagingCode = lastChangedPackage.current?.code;
			// requirementsRef.current[requirementsRef.current.length] = false;
		}

		addNewRegistrationLineApi({ ..._form })
			.then((data) => {
				let line: any = {
					...data,
					inEdit: true,
					// focusedField: focusRef.current?.focusedField,
					// onfocus: dropDown ? false : focusRef.current?.,
				};
				if (data.id === focusRef.current?.id) {
					line.focusedField = focusRef.current?.focusedField;
					line.onfocus = true;
				}
				if (!_form.id) {
					setLineForm((prev) => [...prev, line]);
					return;
				}

				setLineForm((prev) => {
					return prev.map((item) => {
						if (item.id === line.id) {
							return line;
						}
						if (item.id === focusRef.current?.id) {
							item.focusedField = focusRef.current?.focusedField;
							item.onfocus = true;
						} else {
							item.focusedField = undefined;
							item.onfocus = false;
						}
						return item;
					});
				});
				//}
				//  setForm(form.map((data) => (data.id === _form.id ? _form : data)));
			})
			.catch((err) => {
				console.log(err);
			});
	};

	const handleOnChangeItem = (e: GridItemChangeEvent) => {
		if (e.field) {
			if (e.dataItem[e.field] !== e.value) {
				e.dataItem[e.field] = e.value;
				addRegistrationLine(e.dataItem);
			}
		}
	};
	const meetRequirement = (data: any, field: string) => {
		let isPassed = false;
		if (field === "numberOfPieces") {
			if (!data.piecesMandatory || data.piecesMandatory === 0) {
				return true;
			} else if (
				data.piecesMandatory === 1 &&
				(data[field] || data[field] > 0)
			) {
				return true;
			} else {
				return false;
			}
		}
		if (
			data[field] != null &&
			data[field] !== "null" &&
			data[field] !== "" &&
			data[field] !== undefined &&
			data[field] !== "undefined" &&
			data[field] !== "0" &&
			data[field] !== 0
		) {
			isPassed = true;
		}

		return isPassed;
	};

	const startDeletingLine = (props: GridCellProps) => {
		deletingLineRef.current = props.dataItem;
		setShowDeleteLine(true);
	};

	const startDeletingLineMobile = (line: LineForm) => {
		deletingLineRef.current = line as RegistrationLine;
		setShowDeleteLine(true);
	};

	const deleteLine = () => {
		if (!deletingLineRef.current) return;
		deleteTemporaryRegistrationLineApi(
			deletingLineRef.current.id,
			deletingLineRef.current.registrationId,
		)
			.then((response) => {
				if (response.returnIsOK) {
					setLineForm((prev) =>
						prev.filter(
							(item) => item.id !== (deletingLineRef.current?.id as any),
						),
					);
				}
				setShowDeleteLine(!showDeleteLine);
			})
			.catch((err) => {
				if (err?.response?.data) {
					setError(err?.response?.data.returnText);
				} else {
					setError(err.message);
				}
			});
	};

	const handleProductConditionChange = (name: string, value: boolean) => {
		const updated = productConditionFilter.map((item) =>
			item.name === name ? { ...item, value } : item,
		);

		const getValue = (key: string) =>
			updated.find((x) => x.name === key)?.value ?? false;

		updateSalesNoteProductConditionFilterApi({
			fresh: getValue("Fresh"),
			frozen: getValue("Frozen"),
			filet: getValue("Filet"),
			user: getValue("User"),
		})
			.catch((err) => {
				console.log("failed to update product condition filter", err);
			})
			.then(() => {
				setProductConditionFilter(updated);
			});
	};

	useEffect(() => {
		if (addLineRef) {
			addLineRef.current = () => addRegistrationLine(Object());
		}
	});

	useEffect(() => {
		if (lineForm.length > prevFormLengthRef.current) {
			bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
		}
		prevFormLengthRef.current = lineForm.length;
	}, [lineForm.length]);

	return (
		<div style={{ width: "100%", maxWidth: "100%" }}>
			<div className="sales-note-condition-filters" style={{ display: "flex", gap: 10, marginBottom: "1rem" }}>
				{productConditionFilter.map((condition) => (
					<div key={condition.name} className="sales-note-condition-filter-item">
						<CustomCheckbox
							value={condition.value}
							onChange={(value) =>
								handleProductConditionChange(condition.name, value)
							}
							label={t(condition.displayName)}
							containerStyle={{ width: "100%", justifyContent: "space-between" }}
						/>
					</div>
				))}
			</div>
			{isMobileView ? (
				<StepTwoMobileList
					form={lineForm}
					onUpdateLine={(line) => addRegistrationLine(line, true)}
					onSelectPackage={(line) => {
						lastChangedPackage.current = {
							code: line.packagingCode,
							displayName: "",
							name: (line as any).packaging,
						};
						addRegistrationLine(line, true);
					}}
					onDelete={startDeletingLineMobile}
					hasMeetRequirement={meetRequirement}
					showRequirement={showRequirement}
				/>
			) : (
				<CustomGrid
					style={{ width: "100%" }}
					gridColumns={stepTwoGridColumns({
						addLine: (data) => addRegistrationLine(data, true),
						showError: showRequirement,
						hasMeetRequirement: meetRequirement,
						onSelectPackage: (data) => {
							lastChangedPackage.current = {
								code: data.packagingCode,
								displayName: "",
								name: data.packaging,
							};

							addRegistrationLine(data, true);
						},

						startDeletingLine: startDeletingLine,
						data: lineForm || [],
						onChange: (data) => {
							handleOnChangeItem(data);
						},
						onFocus(data) {
							if (focusRef.current) {
								if (
									focusRef.current.focusedField === data.field &&
									focusRef.current.rowIndex === data.rowIndex
								) {
									return;
								}
							}

							focusRef.current = {
								columnIndex: data.columnIndex,
								focusedField: data.field,
								rowIndex: data.rowIndex,
								id: data.dataItem.id,
							};
							const lines = lineForm?.map((item) =>
								item.id === data.dataItem.id
									? {
											...item,
											onfocus: true,
											focusedField: data.field,
									  }
									: { ...item, onfocus: false, focusedField: undefined },
							);
							setLineForm(lines as LineForm[]);
						},
					})}
					data={lineForm}
					className={CLASSNAME.TableNumeric + " " + CLASSNAME.HideOverFlow}
					edit={{ id: true }}
				/>
			)}

			<div ref={bottomRef} />

			<Info
				close={() => setShowDeleteLine(!showDeleteLine)}
				title={t("DeleteLine")}
				show={showDeleteLine}
				description={t("DeleteLineConfirmation")}
				actions={[
					{ text: t("Cancel"), btn: () => setShowDeleteLine(!showDeleteLine) },
					{
						text: t("Yes"),
						btn: deleteLine,
						bgColor: "red",
						textColor: "white",
					},
				]}
			/>
		</div>
	);
};

export default FormStepTwo;
